import React, { useState, useEffect } from 'react';
import { Users, Loader } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function ValetListView() {
  const [valetGroups, setValetGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchValetGroupings();
  }, []);

  const fetchValetGroupings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/valet-bookings/grouped/by-date`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }

      const data = await response.json();
      
      // Group by date then by valet, excluding completed bookings
      const grouped = {};
      if (data.data && Array.isArray(data.data)) {
        data.data.forEach(booking => {
          // Skip completed bookings
          if (booking.status === 'completed') {
            return;
          }
          
          if (!grouped[booking.booking_date]) {
            grouped[booking.booking_date] = {};
          }
          if (!grouped[booking.booking_date][booking.valet_name]) {
            grouped[booking.booking_date][booking.valet_name] = [];
          }
          grouped[booking.booking_date][booking.valet_name].push(booking);
        });
      }
      
      setValetGroups(grouped);
      setError('');
    } catch (err) {
      setError(err.message);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
        Error: {error}
      </div>
    );
  }

  const dateGroups = Object.entries(valetGroups)
    .sort(([dateA], [dateB]) => {
      const a = new Date(dateA.includes('T') ? dateA : dateA + 'T00:00:00Z');
      const b = new Date(dateB.includes('T') ? dateB : dateB + 'T00:00:00Z');
      return a - b;
    });

  if (dateGroups.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-600">No upcoming bookings</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {dateGroups.map(([date, valetData]) => {
        // Parse date correctly - handle both formats
        let dateObj;
        if (date.includes('T')) {
          // ISO format: 2025-11-10T00:00:00.000Z
          dateObj = new Date(date);
        } else {
          // Date string: 2025-11-10
          dateObj = new Date(date + 'T00:00:00Z');
        }
        
        const formattedDate = dateObj.toLocaleDateString('en-US', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });

        return (
          <div key={date}>
            {/* Date Header */}
            <div className="mb-4 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">
                  📅 {formattedDate}
                </h2>
                <div className="h-1 w-20 bg-gradient-to-r from-blue-600 to-blue-400 mt-2 rounded-full"></div>
              </div>
              <button
                onClick={() => window.print()}
                className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition print:hidden"
                title="Print this day's valet list"
              >
                🖨️ Print
              </button>
            </div>

            {/* Valets for this date */}
            <div className="space-y-4">
              {Object.entries(valetData).map(([valetName, bookings]) => {
                const paintProtectionCount = bookings.filter(
                  b => b.paint_protection === 'yes'
                ).length;

                return (
                  <div
                    key={valetName}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition"
                  >
                    {/* Valet Header */}
                    <div className="bg-gradient-to-r from-blue-50 to-slate-50 px-4 py-3 border-b border-slate-200">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold text-slate-900">
                          👤 {valetName}
                        </h3>
                        <span className="text-sm font-medium text-slate-600 bg-white px-3 py-1 rounded-full">
                          {bookings.length} booking{bookings.length !== 1 ? 's' : ''} | 
                          Paint Protection: {paintProtectionCount}/{bookings.length}
                        </span>
                      </div>
                    </div>

                    {/* Bookings List */}
                    <div className="divide-y divide-slate-100">
                      {bookings.map((booking, idx) => (
                        <div
                          key={idx}
                          className="px-4 py-3 hover:bg-slate-50 transition"
                        >
                          <div className="flex justify-between items-start gap-4">
                            {/* Left: Time, Vehicle, Registration, Customer, Sales Exec */}
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-3">
                                <span className="text-lg font-bold text-slate-900">
                                  ⏰ {booking.booking_time}
                                </span>
                                <span className="text-sm font-semibold text-slate-700">
                                  {booking.vehicle_make} {booking.vehicle_model}
                                </span>
                              </div>
                              <div className="text-sm text-slate-600 space-y-1">
                                <div>
                                  <span className="font-medium">Reg:</span> {booking.vehicle_registration}
                                </div>
                                {booking.customer_name && (
                                  <div>
                                    <span className="font-medium">👤 Customer:</span> {booking.customer_name}
                                  </div>
                                )}
                                {booking.sales_executive_name && (
                                  <div>
                                    <span className="font-medium">📊 Sales Exec:</span> {booking.sales_executive_name}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Right: Key, Paint Protection */}
                            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                              <div className="text-right">
                                <div className="text-sm font-semibold text-slate-900">
                                  🔑 {booking.key_number}
                                </div>
                              </div>
                              {booking.paint_protection === 'yes' ? (
                                <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded">
                                  ✓ Paint Protection
                                </span>
                              ) : (
                                <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded">
                                  ✗ No Paint
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Divider */}
            <div className="my-8 border-t-2 border-slate-200"></div>
          </div>
        );
      })}
    </div>
  );
}
